import * as React from 'react';
import { observer } from 'mobx-react';

import { Button } from 'containers/Tiptap/components/tiptap-ui-primitive/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'containers/Tiptap/components/tiptap-ui-primitive/dropdown-menu';
import { ChevronDownIcon } from 'containers/Tiptap/components/tiptap-icons/chevron-down-icon';
import { PlusIcon } from 'containers/Tiptap/components/tiptap-icons/plus-icon';
import { MinusIcon } from 'containers/Tiptap/components/tiptap-icons/minus-icon';

import { useEditorStore } from 'containers/Tiptap/hooks/useEditorStore';
import { useTiptapEditor } from 'containers/Tiptap/hooks/use-tiptap-editor';

import { ZoomLabel } from './styles';
import { ZOOM_OPTIONS, nextZoomPreset, previousZoomPreset } from './zoom-presets';
import { FIT_ZOOM_LABEL, fitZoomPercent } from './zoom-fit';

const applyZoomToEditor = (editor: ReturnType<typeof useTiptapEditor>['editor'], zoomPercent: number): void => {
  const commands = editor?.commands as Record<string, unknown> | undefined;
  if (editor && commands && typeof commands.setZoom === 'function') {
    editor.commands.setZoom(zoomPercent / 100);
  }
};

function ZoomControlsComponent(): React.ReactNode {
  const editorStore = useEditorStore();
  const { editor } = useTiptapEditor();
  const [isOpen, setIsOpen] = React.useState(false);

  const zoom = editorStore.zoomPercent;
  const [isFitted, setIsFitted] = React.useState(false);

  const setZoom = React.useCallback(
    (next: number) => {
      setIsFitted(false);
      applyZoomToEditor(editor, next);
    },
    [editor]
  );

  const fitToWidth = React.useCallback(() => {
    const container = editor?.view.dom.closest('.notion-like-editor-layout');
    const pageFormat = editor?.storage?.pages?.pageFormat as { width?: number } | undefined;
    const pageWidthPx = pageFormat?.width;
    if (!container || typeof pageWidthPx !== 'number') return;

    applyZoomToEditor(editor, fitZoomPercent({ availableWidthPx: container.clientWidth, pageWidthPx }));
    setIsFitted(true);
  }, [editor]);

  React.useEffect(() => {
    if (!isFitted) return;
    const container = editor?.view.dom.closest('.notion-like-editor-layout');
    if (!container) return;

    const observer = new ResizeObserver(() => setIsFitted(false));
    observer.observe(container);
    return () => observer.disconnect();
  }, [editor, isFitted]);

  return (
    <>
      <Button
        type="button"
        data-style="ghost"
        tooltip="Zoom out"
        aria-label="Zoom out"
        onClick={() => setZoom(previousZoomPreset(zoom))}
      >
        <MinusIcon className="tiptap-button-icon" />
      </Button>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            data-style="ghost"
            role="button"
            tabIndex={-1}
            aria-label="Set zoom level"
            tooltip="Zoom level"
          >
            <ZoomLabel>{isFitted ? FIT_ZOOM_LABEL : `${zoom}%`}</ZoomLabel>
            <ChevronDownIcon className="tiptap-button-dropdown-small" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuGroup>
            <DropdownMenuItem
              asChild
              onSelect={() => {
                fitToWidth();
                setIsOpen(false);
              }}
            >
              <Button
                type="button"
                data-style="ghost"
                role="button"
                tabIndex={-1}
                aria-label="Fit page to width"
                tooltip=""
              >
                <span className="tiptap-button-text">{FIT_ZOOM_LABEL}</span>
              </Button>
            </DropdownMenuItem>
            {ZOOM_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                asChild
                onSelect={() => {
                  setZoom(opt.value);
                  setIsOpen(false);
                }}
              >
                <Button
                  type="button"
                  data-style="ghost"
                  role="button"
                  tabIndex={-1}
                  aria-label={opt.label}
                  tooltip=""
                >
                  <span className="tiptap-button-text">{opt.label}</span>
                </Button>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        type="button"
        data-style="ghost"
        tooltip="Zoom in"
        aria-label="Zoom in"
        onClick={() => setZoom(nextZoomPreset(zoom))}
      >
        <PlusIcon className="tiptap-button-icon" />
      </Button>
    </>
  );
}

export const ZoomControls = observer(ZoomControlsComponent);
